import { useRouter } from "@core/Api/useRouter";
import Path from "@core/FileProvider/Path/Path";
import type { ArticleDiffData } from "@core/SitePresenter/types/ArticlePage";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ArticleViewService from "@core-ui/ContextServices/views/articleView/ArticleViewService";
import WorkspaceService from "@core-ui/ContextServices/Workspace";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { useDiffStore } from "@core-ui/stores/DiffStore/DiffStore.provider";
import {
	attachLiveDiff,
	closeLiveDiffSession,
	detachLiveDiff,
	enableLiveDiff,
	isLiveDiffStatus,
	type LiveDiffView,
	setLiveDiffWorkspace,
	useLiveDiffSession,
	useLiveDiffStore,
} from "@ext/git/core/Diff/logic/live/liveDiffStore";
import { GitTreeScopeParser } from "@ext/versioning/GitTreeScopeParser";
import { useEffect, useRef } from "react";

const isDiffOfArticle = (diff: ArticleDiffData, catalogName: string, articlePath: string) => {
	const filePath = diff.sideBarData?.data?.filePath;
	return !!filePath && Path.join(catalogName, filePath.path) === articlePath;
};

interface LiveDiffBindingProps {
	/** Must match `articleProps.ref.path`; the session is keyed by it. */
	articlePath: string;
	view: LiveDiffView;
}

export const useLiveDiffBinding = ({ articlePath, view }: LiveDiffBindingProps) => {
	const session = useLiveDiffSession();
	const setDiff = useDiffStore((state) => state.setDiff);
	const pageDiff = useDiffStore((state) => state.diff);
	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const apiUrlCreator = ApiUrlCreatorService.value;
	const router = useRouter();
	const workspacePath = WorkspaceService.current()?.path ?? "";
	const bootstrapped = useRef(false);

	useEffect(() => {
		attachLiveDiff();
		return () => detachLiveDiff();
	}, []);

	// Must run before the bootstrap effect: the cache key includes the workspace.
	useEffect(() => setLiveDiffWorkspace(workspacePath), [workspacePath]);

	// The old editor stays mounted while the next article loads, so close on route change, not on cleanup.
	const path = router.path;
	useEffect(() => {
		const current = useLiveDiffStore.getState().session;
		if (current && current.path !== path) closeLiveDiffSession();
	}, [path]);

	// Once per mount: `pageDiff` outlives turning diff off and must not re-enable it.
	// biome-ignore lint/correctness/useExhaustiveDependencies: first entry only, the rest is read at call time
	useEffect(() => {
		if (bootstrapped.current || !pageDiff || !catalogName || !articlePath) return;
		// The catalog-scoped store still holds the previous article's diff on the first render.
		if (!isDiffOfArticle(pageDiff, catalogName, articlePath)) return;
		bootstrapped.current = true;
		if (pageDiff.scope || !isLiveDiffStatus(pageDiff.sideBarData?.data?.status)) return;
		void enableLiveDiff({
			catalogName,
			articlePath,
			oldScope: GitTreeScopeParser.toString(pageDiff.oldScope ?? "HEAD"),
			view,
			known: pageDiff,
			apiUrlCreator,
			router,
		});
	}, [pageDiff, catalogName, articlePath]);

	const on = session?.status === "on";

	// Don't touch the store before a session exists or while it loads: the toolbar button would flicker.
	const loading = session?.status === "loading";
	const seen = useRef(false);
	useEffect(() => {
		if (session) seen.current = true;
		if (loading) return;
		if (on) {
			setDiff(session.data);
			return;
		}
		if (!seen.current) return;
		seen.current = false;
		setDiff(undefined);
		ArticleViewService.setDefaultView();
	}, [on, loading, session, setDiff]);

	// Without this the next article would enable diff from this article's data.
	useEffect(
		() => () => {
			if (seen.current) setDiff(undefined);
		},
		[setDiff],
	);

	return { session, on };
};
